import type { EstimateField, EstimateLineItem } from '@/types/estimate';

/**
 * Pure formatting helpers for the Estimate page. No React imports
 * so both the editor and read-only contract views can reuse.
 */

export function formatCurrency(decimalAsString: string): string {
  const n = parseFloat(decimalAsString);
  if (!Number.isFinite(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(n);
}

export function formatQuantity(qty: string, unit: string, customUnit?: string | null): string {
  const n = parseFloat(qty);
  if (!Number.isFinite(n)) return '0';
  const pretty = n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  const unitLabel = unit === 'CUSTOM' && customUnit ? customUnit : unit;
  return `${pretty} ${unitLabel}`;
}

/**
 * Format-fix batch 2026-07 — read-only answer display. Prefers the
 * server-computed `answerDisplay` (option labels, Yes/No, units, folded
 * repeatable rows — one formatting shared with PDF/snapshot/contract);
 * the local fallback below covers pre-batch snapshots that lack it and
 * mirrors the same rules. Never dumps raw JSON.
 */
export function formatAnswer(field: EstimateField): string {
  if (field.answerDisplay != null && field.answerDisplay !== '') {
    return field.answerDisplay;
  }
  const value = field.answerValue;
  if (value == null || value === '') return '—';
  const optionLabel = (v: unknown): string => {
    if (typeof v === 'string') {
      const hit = field.options?.find((o) => o.value === v);
      if (hit) return hit.label;
    }
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
  };
  switch (field.inputType) {
    case 'BOOLEAN':
      return value === true ? 'Yes' : 'No';
    case 'SINGLE_SELECT':
      return optionLabel(value);
    case 'MULTI_SELECT':
      return Array.isArray(value)
        ? value.map(optionLabel).join(', ')
        : optionLabel(value);
    case 'NUMBER':
      return field.unit ? `${String(value)} ${field.unit}` : String(value);
    default:
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (Array.isArray(value)) {
        // Legacy repeatable-block rows without a server answerDisplay:
        // fold per row instead of printing raw JSON.
        return value
          .map((row, i) => {
            if (row != null && typeof row === 'object' && !Array.isArray(row)) {
              const parts = Object.entries(row as Record<string, unknown>)
                .filter(([, v]) => v != null && v !== '')
                .map(([k, v]) => `${k}: ${optionLabel(v)}`);
              return `#${i + 1} — ${parts.length > 0 ? parts.join(', ') : 'empty'}`;
            }
            return optionLabel(row);
          })
          .join('; ');
      }
      // Unknown object shape — never a JSON dump on a client surface.
      if (typeof value === 'object') return '—';
      return String(value);
  }
}

export function effectiveUnitCost(cost: {
  unitCostSnapshot: string;
  unitCostOverride?: string | null;
}): number {
  const override =
    cost.unitCostOverride != null ? parseFloat(cost.unitCostOverride) : NaN;
  if (Number.isFinite(override)) return override;
  return parseFloat(cost.unitCostSnapshot);
}

export function lineItemTypeLabel(li: EstimateLineItem): string {
  return li.type === 'MATERIAL' ? 'Material' : 'Service';
}

/**
 * Founder 2026-08-05 (bug 3, casing) — Project Detail answers display with
 * inconsistent casing because they render exactly as stored ("stamped
 * concrete" next to "Stamped Concrete"). Normalize at DISPLAY only: title-
 * case each word, guarding trade acronyms and unit-ish tokens, and leave
 * anything containing a digit alone ("4x8", "30 in"). Stored data is never
 * touched, and edit inputs show the raw value.
 */
const CASING_GUARD: Record<string, string> = {
  pvc: 'PVC', hoa: 'HOA', pt: 'PT', mep: 'MEP', hvac: 'HVAC', led: 'LED',
  gfci: 'GFCI', irc: 'IRC', tsr: 'TSR', llc: 'LLC', diy: 'DIY', uv: 'UV',
  of: 'of', and: 'and', or: 'or', the: 'the', to: 'to', with: 'with',
  // Units stay lowercase mid-phrase ("8 ft gate", "30 in rise").
  ft: 'ft', in: 'in', sqft: 'sqft', lf: 'lf', ea: 'ea', mm: 'mm', cm: 'cm',
};

export function formatAnswerDisplay(raw: string): string {
  if (!raw || !raw.trim()) return raw;
  return raw
    .split(/(\s+|\/|—|–|-)/)
    .map((token, i) => {
      if (/^\s*$/.test(token) || /^[/—–-]$/.test(token)) return token;
      const lower = token.toLowerCase();
      if (lower in CASING_GUARD) {
        const guarded = CASING_GUARD[lower];
        // Small joining words stay lowercase mid-phrase but cap at the front.
        return i === 0 && guarded === lower
          ? guarded.charAt(0).toUpperCase() + guarded.slice(1)
          : guarded;
      }
      if (/\d/.test(token)) return token;
      if (/^[A-Z0-9&().]+$/.test(token) && token.length <= 4) return token;
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join('');
}
